import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Location component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconLocation extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12 2.60498C10.3362 2.60498 8.74052 3.26593 7.56402 4.44243C6.38751 5.61894 5.72656 7.21462 5.72656 8.87844C5.72656 9.67434 6.10124 10.8609 6.75489 12.2765C7.39528 13.6633 8.25564 15.1711 9.12565 16.5738C9.99407 17.9738 10.8637 19.2557 11.5169 20.1883C11.6959 20.4439 11.8584 20.6729 12 20.8707C12.1416 20.6729 12.3042 20.4439 12.4831 20.1883C13.1363 19.2557 14.006 17.9738 14.8744 16.5738C15.7444 15.1711 16.6048 13.6633 17.2452 12.2765C17.8988 10.8609 18.2735 9.67434 18.2735 8.87844C18.2735 7.21462 17.6125 5.61894 16.436 4.44243C15.2595 3.26593 13.6638 2.60498 12 2.60498ZM12 22.145C11.3997 22.5945 11.3996 22.5944 11.3995 22.5943L11.3939 22.5867L11.3781 22.5656L11.3178 22.4843C11.2654 22.4132 11.1888 22.3091 11.0917 22.1754C10.8974 21.9082 10.6204 21.523 10.2883 21.0489C9.62459 20.1012 8.73838 18.7952 7.85094 17.3644C6.96508 15.9362 6.06958 14.3703 5.39307 12.9053C4.72982 11.469 4.22656 10.0257 4.22656 8.87844C4.22656 6.81679 5.04555 4.83958 6.50336 3.38177C7.96116 1.92397 9.93837 1.10498 12 1.10498C14.0617 1.10498 16.0389 1.92397 17.4967 3.38177C18.9545 4.83958 19.7735 6.81679 19.7735 8.87844C19.7735 10.0257 19.2702 11.469 18.607 12.9053C17.9305 14.3703 17.035 15.9362 16.1491 17.3644C15.2617 18.7952 14.3755 20.1012 13.7118 21.0489C13.3797 21.523 13.1027 21.9082 12.9084 22.1754C12.8112 22.3091 12.7347 22.4132 12.6822 22.4843L12.622 22.5656L12.6062 22.5867L12.6009 22.5938C12.6008 22.594 12.6004 22.5945 12 22.145ZM12 22.145L12.6009 22.5938C12.4593 22.783 12.2363 22.895 12 22.895C11.7637 22.895 11.5411 22.7834 11.3995 22.5943L12 22.145Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.0003 7.28729C11.1216 7.28729 10.4092 7.99968 10.4092 8.87845C10.4092 9.75722 11.1216 10.4696 12.0003 10.4696C12.8791 10.4696 13.5915 9.75722 13.5915 8.87845C13.5915 7.99968 12.8791 7.28729 12.0003 7.28729ZM8.90918 8.87845C8.90918 7.17125 10.2931 5.78729 12.0003 5.78729C13.7075 5.78729 15.0915 7.17125 15.0915 8.87845C15.0915 10.5856 13.7075 11.9696 12.0003 11.9696C10.2931 11.9696 8.90918 10.5856 8.90918 8.87845Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-location') === undefined) {
  customElements.define('cat-icon-location', BlocksIconLocation);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-location': BlocksIconLocation;
  }
}
